import { Component, inject, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { BrandService } from '../services/brand.service';
import { Brand } from '../models/brand.model';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-brand-management',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './brand-management.component.html',
  styleUrls: ['./brand-management.component.css']
})
export class BrandManagementComponent implements OnInit {
  private brandService = inject(BrandService);

  brands$: Observable<Brand[]> | null = null;
  brands: Brand[] = [];

  isModalOpen = false;
  isSaving = false;
  editingId: string | null = null;
  errorMessage: string | null = null;

  newBrand: Brand = { name: '', description: '', isActive: true };

  sortColumn: string = 'name';
  sortDirection: 'asc' | 'desc' = 'asc';

  ngOnInit() {
    this.brands$ = this.brandService.getBrands();
    this.brands$.subscribe(data => {
      this.brands = data;
      this.sortBrands();
    });
  }

  openAddModal() {
    this.cancelEdit();
    this.isModalOpen = true;
  }

  editBrand(brand: Brand) {
    this.newBrand = { ...brand };
    this.editingId = brand.id || null;
    this.errorMessage = null;
    this.isModalOpen = true;
  }

  cancelEdit() {
    this.newBrand = { name: '', description: '', isActive: true };
    this.editingId = null;
    this.errorMessage = null;
    this.isModalOpen = false;
  }

  async saveBrand() {
    this.errorMessage = null;
    if (!this.newBrand.name) {
      this.errorMessage = 'Brand Name is required.';
      return;
    }

    this.isSaving = true;
    try {
      if (this.editingId) {
        await this.brandService.updateBrand(this.editingId, this.newBrand);
      } else {
        await this.brandService.addBrand(this.newBrand);
      }
      this.cancelEdit();
    } catch (error: any) {
      console.error('Error saving brand:', error);
      this.errorMessage = 'Failed to save brand.';
    } finally {
      this.isSaving = false;
    }
  }

  async deleteBrand(id: string | undefined) {
    if (!id) return;
    if (confirm('Are you sure you want to delete this brand?')) {
      try {
        await this.brandService.deleteBrand(id);
      } catch (error) {
        console.error('Error deleting brand:', error);
        alert('Failed to delete brand.');
      }
    }
  }

  onSort(column: string) {
    if (this.sortColumn === column) {
      this.sortDirection = this.sortDirection === 'asc' ? 'desc' : 'asc';
    } else {
      this.sortColumn = column;
      this.sortDirection = 'asc';
    }
    this.sortBrands();
  }

  private sortBrands() {
    if (!this.brands) return;
    this.brands.sort((a: any, b: any) => {
      const valA = a[this.sortColumn];
      const valB = b[this.sortColumn];
      let comparison = 0;
      
      if (valA == null) comparison = -1;
      else if (valB == null) comparison = 1;
      else if (typeof valA === 'boolean' && typeof valB === 'boolean') {
        comparison = (valA === valB) ? 0 : valA ? -1 : 1;
      } else {
        comparison = String(valA).toLowerCase().localeCompare(String(valB).toLowerCase());
      }

      return this.sortDirection === 'asc' ? comparison : -comparison;
    });
  }
}
